import {
  createReservation,
  getServices,
  getAvailability,
  getBranches,
  getReferrals,
  getWhois,
  getSettings,
} from "repositories/reservations/reservationRepository";
import { getPersonalData } from "repositories/reservations/personalRepository";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getAvailabilityParam } from "repositories/reservations/reservationRepository.param";
import { QUERY_STALE_TIME } from "./queryClient";

export const MAX_AVAILABILITY_MONTH_RETRIES = 6;

export const useGetSettingsQuery = (customerId: string) =>
  useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(customerId).then((res) => res.data.settings ?? null),
    staleTime: QUERY_STALE_TIME,
    enabled: !!customerId,
  });

export const useGetServicesQuery = (customerId) =>
  useQuery({
    queryKey: ["services"],
    queryFn: () => getServices(customerId).then((res) => res.data.result),
    staleTime: QUERY_STALE_TIME,
    enabled: !!customerId,
  });

export const useGetReferralsQuery = (customerId) =>
  useQuery({
    queryKey: ["referrals"],
    queryFn: () => getReferrals(customerId).then((res) => res.data.result),
    staleTime: QUERY_STALE_TIME,
    enabled: !!customerId,
  });

export const useGetBranchesQuery = (customerId) =>
  useQuery({
    queryKey: ["branches"],
    queryFn: () => getBranches(customerId).then((res) => res.data.result),
    staleTime: QUERY_STALE_TIME,
    enabled: !!customerId,
  });

export const useGetFinanceDetailsQuery = (customerId: string, userId: string, branchId: string) =>
  useQuery({
    queryKey: ["financeDetails", customerId, userId, branchId],
    queryFn: () => getWhois(customerId, userId, branchId).then((res) => res.data.result),
    staleTime: QUERY_STALE_TIME,
    enabled: !!customerId && !!userId && !!branchId,
  });

export const useGetPersonalDataQuery = (customerId: string, userId: string) =>
  useQuery({
    queryKey: ["personalData", customerId, userId],
    queryFn: () => getPersonalData(customerId, userId),
    staleTime: QUERY_STALE_TIME,
    enabled: !!customerId && !!userId,
  });

export const useGetAvailabilityQuery = (input: getAvailabilityParam, enabled = true) => {
  return useQuery({
    queryKey: [input],
    queryFn: async () => {
      const initialDateFrom = dayjs(input.dateFrom);
      const initialDateTo = dayjs(input.dateTo);

      const payload = {
        ...input,
        dateFrom: initialDateFrom.format("YYYY-MM-DD"),
        dateTo: initialDateTo.format("YYYY-MM-DD"),
      };

      const result = await getAvailability(payload).then((res) => res.data.result);

      if (!Array.isArray(result) || result.length > 0) {
        return result;
      }
      return [];
    },
    enabled,
    staleTime: QUERY_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};
export const useCreateReservationMutation = () => useMutation({ mutationFn: createReservation });
